/**
 * 홈 답 카드의 글 — 수 표기 · 틀 문장 · 「전체 보기」 이름 · 수의 구간. 서버(틀 문장)와 화면(단추)이 같은 표기를 쓴다.
 *
 * 🔑 수는 그 화면 결과 머리(`ResultHeader`)와 같은 표기다 — 줄이지 않고 천 단위만 끊는다(`4,320`).
 * ⚠️ 서비스 한계·데이터 양을 말하는 글을 짓지 않는다 — 0건은 그 화면의 빈 결과 문구와 같다.
 * ⚠️ `'use client'` 를 붙이지 않는다 — 순수 함수다.
 */

import type { CompactNumberOptions } from 'src/utils/format/format-compact-number';
import type { AskReading, AskScreenId, AskAnswerScreenId } from 'src/types/ask/ask-ops';

import { toAskChips } from 'src/utils/ask/ask-chips';
import { clipAtWord } from 'src/utils/text/clip-text';
import { hasFinalConsonant } from 'src/utils/format/korean-particle';
import { formatCompactNumber } from 'src/utils/format/format-compact-number';

import { ASK_SCREENS, ASK_EVENT_ANCHOR_NAMES, ASK_HOME_ANSWER_SCREENS, ASK_GUEST_ANSWER_SCREENS } from 'src/constants/ask-screens';

/** 답 카드의 수 표기 — 결과 머리와 같은 ko-KR 천단위 · 0 은 「0」 */
export const ASK_ANSWER_COUNT_FORMAT: CompactNumberOptions = {
  nonPositive: 'zero',
  compactFrom: Number.POSITIVE_INFINITY,
};

/** 화면마다 세는 것의 이름 — 틀 문장과 빈 결과 문구에 들어간다 */
export const ASK_ANSWER_NOUN: Record<AskAnswerScreenId, string> = {
  events: '이벤트',
  ads: '광고 소재',
  social: '소셜 글',
  contents: '콘텐츠',
  products: '상품',
  brands: '브랜드',
  creators: '크리에이터',
  brand_mentions: '멘션',
};

/** 화면마다 수의 단위 — 그 화면 머리·발치가 적는 단위(브랜드 허브 「상위 N개」 · 크리에이터 허브 「N명」) */
export const ASK_ANSWER_UNIT: Record<AskAnswerScreenId, string> = {
  events: '건',
  ads: '건',
  social: '건',
  contents: '건',
  products: '건',
  brands: '개',
  creators: '명',
  brand_mentions: '건',
};

/**
 * 「전체 보기」에 수를 적지 않는 화면 — 콘텐츠 허브는 머리에 수가 없고(원장마다 따로 편다),
 * 판매몰 상품은 들어간 뒤 실시간 판매몰 결과가 더 붙어 머리 수가 늘 수 있다.
 */
const UNCOUNTED_SCREENS: readonly AskAnswerScreenId[] = ['contents', 'products'];

/** 수가 순위 목록의 길이인 화면 — 목록 발치처럼 「상위 N개」로 적는다 */
const RANKED_SCREENS: readonly AskAnswerScreenId[] = ['brands'];

/** 홈에서 먼저 답하는 화면인가 */
export function isAskAnswerScreen(screen: AskScreenId | 'unsupported'): screen is AskAnswerScreenId {
  return (ASK_HOME_ANSWER_SCREENS as readonly string[]).includes(screen);
}

/**
 * 홈에서 먼저 답하는 읽기인가 — 화면이 답하는 화면이어도 **목록이 아닌 자리**로 여는 읽기는 지금처럼 옮긴다.
 * 판매몰 상품은 검색어·사전 이름이 있거나 판매몰 하나의 랭킹(`isAskProductBoardReading`)일 때만 답한다 — 그 밖의 보드는 옮긴다.
 * 브랜드 허브에 브랜드 하나(`brand`)는 그 브랜드의 개요로 답한다.
 */
export function isAskAnswerReading(reading: AskReading): boolean {
  if (!isAskAnswerScreen(reading.screen)) return false;
  if (reading.screen === 'products') return PRODUCT_SEARCH_KEYS.some((key) => hasText(reading, key)) || isAskProductBoardReading(reading);
  return true;
}

/**
 * 판매몰 하나의 랭킹을 묻는 읽기인가 — 검색어·사전 이름 없이 판매몰(`mall`)만 고르고, 그 밖에는 정렬 「랭킹 높은순」(`sort: 'rank'`) · 지역(`region`)뿐.
 * 🔑 답은 보드의 그 판매몰 칸이다 — 다른 좁히기(가격대 · 신상품 · 태그 …)는 보드가 받지 않아 걸리면 답하지 않는다.
 */
export function isAskProductBoardReading(reading: AskReading): boolean {
  if (reading.screen !== 'products' || !hasText(reading, 'mall')) return false;
  if (PRODUCT_SEARCH_KEYS.some((key) => hasText(reading, key))) return false;
  if (reading.filters.sort !== undefined && reading.filters.sort !== 'rank') return false;
  return Object.keys(reading.filters).every((key) => PRODUCT_BOARD_KEYS.includes(key));
}

/** 비회원에게도 답 카드를 세우는 읽기인가 — 홈이 답하는 읽기 중 비회원 화면(`ASK_GUEST_ANSWER_SCREENS`)의 것만 */
export function isAskGuestAnswerReading(reading: AskReading): boolean {
  return isAskAnswerReading(reading) && (ASK_GUEST_ANSWER_SCREENS as readonly string[]).includes(reading.screen);
}

/**
 * 답 카드의 조건 이름 — 칩 줄과 같은 이름(`toAskChips`)이고 틀 문장·모델 사실에 쓴다.
 * 🔑 시장 말 검색어(「검색어 sunscreen (영어)」)는 사용자가 친 한국어(「‘선크림’」)로 적는다 — 모델이 영어 낱말을 문장에 옮기지 않게.
 * 🔑 칩 틀이 없는 글자 칸(검색어·키워드)은 따옴표로 감싼다(「‘쿠션’」).
 * 🔑 제품군은 짧은 이름으로 적는다(「제품군 마스크 › 시트마스크/마스크팩」 → 「마스크팩」 — `shortenAskCategory`).
 * @param keys 이 화면이 실제로 건 칸만 — 없으면 칩 전부
 */
export function askAnswerConditionLabels(reading: AskReading, keys?: readonly string[]): string[] {
  const shortened = shortenAskCategory(reading);
  const localWord = (key: string) => (reading.local ? reading.choices?.find((one) => one.options.some((option) => option.key === key))?.word ?? '' : '');
  const chips = toAskChips(reading).filter((chip) => !keys || keys.includes(chip.key));
  const named = (word: string) => chips.some((chip) => chip.key !== 'q' && chip.key !== 'keyword' && chip.label.includes(word));
  return chips
    .map((chip) => {
      if (chip.key === 'category' && shortened) return shortened;
      if (chip.key !== 'q' && chip.key !== 'keyword') return chip.label;
      const localized = Boolean(reading.local && reading.labels?.[chip.key]);
      const source = localized ? localWord(chip.key) : '';
      // 시장 말이 옮긴 한국어가 이미 다른 칩(제품군 두피앰플)에 있으면 한 번만 적는다
      if (source) return named(source) ? '' : `‘${source}’`;
      if (localized) return '';
      return `‘${String(reading.filters[chip.key] ?? '').trim()}’`;
    })
    .filter((label) => label && label !== '‘’');
}

/**
 * 제품군 칩의 짧은 이름 — 「마스크 › 시트마스크/마스크팩」의 끝 갈래, 갈래가 「/」로 여럿이면 질문에 든 것(없으면 앞의 것).
 * @returns 제품군이 없으면 빈 글자
 */
export function shortenAskCategory(reading: AskReading): string {
  const value = reading.filters.category;
  if (value === undefined) return '';
  const label = reading.labels?.category ?? String(value);
  const leaf = label.split('›').pop()?.trim() ?? '';
  const parts = leaf.split('/').map((one) => one.trim()).filter(Boolean);
  const flat = reading.question.replace(/\s+/g, '');
  return parts.find((one) => flat.includes(one.replace(/\s+/g, ''))) ?? parts[0] ?? '';
}

/** @returns `4,320` · 못 읽으면 `0` */
export function formatAskAnswerCount(total: number): string {
  return formatCompactNumber(total, { ...ASK_ANSWER_COUNT_FORMAT, fallback: '0' });
}

/**
 * 수 + 단위 — 「178건」 · 「35명」 · 브랜드 허브는 순위 목록이라 「상위 200개」 · 조회 상한에 닿았으면 「6,000건 이상」.
 * @param capped 수가 조회 상한(플랜 문서 상한 · 목록 창)에 닿았나
 */
export function formatAskAnswerTotal(screen: AskAnswerScreenId, total: number, capped = false): string {
  const counted = `${formatAskAnswerCount(total)}${ASK_ANSWER_UNIT[screen]}`;
  if (RANKED_SCREENS.includes(screen)) return `상위 ${counted}`;
  return capped ? `${counted} 이상` : counted;
}

/**
 * 모델이 쓰지 못했을 때의 문장 — 「미국 · 팝업·오프라인 이벤트 178건」.
 * @param conditions 실제로 건 조건의 이름(시장·유형·브랜드·검색어 …) — 차례 그대로 잇는다
 * @param tail 수 뒤에 붙는 한마디(화면마다 · 「최근 30일」처럼 수의 기준) — 없으면 수로 끝난다
 * @param capped 수가 조회 상한에 닿았나 — 「6,000건 이상」
 * @returns 0건이면 그 화면의 빈 결과 문구(「조건에 맞는 이벤트가 없어요」)
 */
export function askAnswerTemplate(screen: AskAnswerScreenId, total: number, conditions: readonly string[], tail = '', capped = false): string {
  const noun = ASK_ANSWER_NOUN[screen];
  if (total <= 0) return `조건에 맞는 ${noun}${hasFinalConsonant(noun) ? '이' : '가'} 없어요`;
  const head = conditions.filter(Boolean).join(' · ');
  return `${head ? `${head} ` : ''}${noun} ${formatAskAnswerTotal(screen, total, capped)}${tail ? ` · ${tail}` : ''}`;
}

/**
 * 「전체 보기」 단추의 이름 — 화면 이름 없이 질문의 대상으로 적는다: 「선크림 브랜드 상위 199개 모두 보기」 · 「롬앤 소셜 글 6,000건 이상 모두 보기」.
 * @param capped 수가 조회 상한에 닿았나
 * @param subject 질문의 대상(`askAnswerSubject`) — 비면 세는 것의 이름만(「소셜 글 178건 모두 보기」)
 * @returns 0건이면 「조건 바꿔 다시 찾기」 · 수를 모르면(막힘) 「이벤트 캘린더 열기」(막힌 것은 화면이라 화면 이름) ·
 *   수를 적지 않는 화면은 「선크림 상품 모두 보기」
 */
export function askAnswerOpenLabel(screen: AskAnswerScreenId, total: number | null, capped = false, subject = ''): string {
  if (total === null) return `${ASK_SCREENS[screen].title} 열기`;
  if (total <= 0) return '조건 바꿔 다시 찾기';
  const what = askAnswerSubjectNoun(screen, subject);
  if (UNCOUNTED_SCREENS.includes(screen)) return `${what} 모두 보기`;
  return `${what} ${formatAskAnswerTotal(screen, total, capped)} 모두 보기`;
}

/** 끝 단추에 수를 적는 화면인가(`UNCOUNTED_SCREENS` 밖) */
export function isAskCountedScreen(screen: AskAnswerScreenId): boolean {
  return !UNCOUNTED_SCREENS.includes(screen);
}

/**
 * 대상 + 세는 것 — 「선크림 브랜드」 · 대상이 없으면 「브랜드」.
 * 🔑 대상 바로 뒤에 조사를 붙이지 않는 꼴이다(로마자 이름의 받침 읽기가 갈려도 틀리지 않는다) — 조사는 세는 것 뒤에 붙인다.
 */
export function askAnswerSubjectNoun(screen: AskAnswerScreenId, subject = ''): string {
  return subject ? `${subject} ${ASK_ANSWER_NOUN[screen]}` : ASK_ANSWER_NOUN[screen];
}

/** 질문의 대상 글자 수 상한 — 넘으면 다음 칸으로, 다 넘으면 대상 없이(넓은 폭에서 단추가 한 줄에 남는다) */
export const ASK_ANSWER_SUBJECT_MAX = 12;

/**
 * 화면마다 대상으로 쓰는 칸 — 앞이 더 좁은(구체적인) 것. 기간·정렬·채널·국가 같은 거르기는 대상이 아니다.
 * 브랜드 멘션은 조건을 받지 않아 대상이 없다.
 */
export const ASK_ANSWER_SUBJECT_KEYS: Record<AskAnswerScreenId, readonly string[]> = {
  events: ['brand', 'q', 'anchor'],
  ads: ['brand', 'q', 'concern', 'category'],
  social: ['brand', 'q', 'ingredient', 'concern', 'category'],
  contents: ['q'],
  products: ['brand', 'q', 'tagIngredient', 'tagConcern', 'category'],
  brands: ['brand', 'category'],
  creators: ['keyword'],
  brand_mentions: [],
};

/**
 * 답 끝 장(한 줄 · 단추)에 적는 질문의 대상 — 읽기에서 코드로 고른다(모델을 다시 부르지 않는다).
 * 브랜드 「롬앤」 · 검색어 「쿠션」 · 제품군의 짧은 이름 「선크림」 · 이벤트 유형 하나 「팝업·오프라인」.
 * 🔑 시장 말 검색어(「sunscreen」)는 사용자가 친 한국어로 적는다 — 옮긴 말이 없으면 그 칸을 건너뛴다.
 * @returns 대상이 없거나 다 길면 빈 글자 — 부르는 쪽은 「이 질문에 맞는 …」으로 적는다
 */
export function askAnswerSubject(reading: AskReading): string {
  if (!isAskAnswerScreen(reading.screen)) return '';
  for (const key of ASK_ANSWER_SUBJECT_KEYS[reading.screen]) {
    const value = subjectValue(reading, key).replace(/\s+/g, ' ').trim();
    if (value && value.length <= ASK_ANSWER_SUBJECT_MAX) return value;
  }
  return '';
}

/** 활동 기록의 수 구간 — 정확한 수는 따로 싣는다 */
export type AskAnswerCountBucket = '0' | '1-9' | '10-99' | '100-999' | '1000+';

export function askAnswerCountBucket(total: number): AskAnswerCountBucket {
  if (!(total > 0)) return '0';
  if (total < 10) return '1-9';
  if (total < 100) return '10-99';
  if (total < 1000) return '100-999';
  return '1000+';
}

/** 답 문장에 드는 상품 이름의 글자 수 상한(「…」 뺀) */
export const ASK_PRODUCT_NAME_MAX = 40;

/**
 * 답 문장에 드는 상품 이름 — 브랜드를 따로 적으므로 앞에 붙은 브랜드(「[롬앤]」·「롬앤 」)를 떼고, 길면 첫 마디(`,`·` - `·` | ` 앞)를 쓰고,
 * 그래도 길면 낱말 경계에서 자른다(`clipAtWord` — 잘랐을 때만 「…」).
 * @param brand 같은 상품의 브랜드 — 비면 떼지 않는다
 */
export function shortAskProductName(title: string, brand = ''): string {
  const flat = String(title ?? '').replace(/\s+/g, ' ').trim();
  const name = stripBrandPrefix(flat, brand) || flat;
  if (name.length <= ASK_PRODUCT_NAME_MAX) return name;
  const clause = name.split(CLAUSE_BREAK_RE)[0].trim();
  if (clause.length >= CLAUSE_MIN && clause.length <= ASK_PRODUCT_NAME_MAX) return clause;
  return clipAtWord(clause.length >= CLAUSE_MIN ? clause : name, ASK_PRODUCT_NAME_MAX);
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 판매몰 상품을 검색으로 답하게 하는 칸 */
const PRODUCT_SEARCH_KEYS = ['q', 'brand', 'ingredient', 'concern', 'category'] as const;

/** 판매몰 하나의 랭킹 읽기가 가질 수 있는 칸 */
const PRODUCT_BOARD_KEYS: readonly string[] = ['mall', 'sort', 'region'];

function hasText(reading: AskReading, key: string): boolean {
  return typeof reading.filters[key] === 'string' && String(reading.filters[key]).trim() !== '';
}

/** 대상 칸 하나의 값 — 칩 이름표(`labels`)가 먼저 · 이벤트 유형은 하나일 때만 · 빈 칸은 빈 글자 */
function subjectValue(reading: AskReading, key: string): string {
  const raw = reading.filters[key];
  if (raw === undefined || raw === null || typeof raw === 'boolean') return '';
  if (key === 'category') return shortenAskCategory(reading);
  if (key === 'anchor') {
    const list = Array.isArray(raw) ? raw : [String(raw)];
    return list.length === 1 ? (ASK_EVENT_ANCHOR_NAMES[list[0]] ?? '') : '';
  }
  if (Array.isArray(raw)) return '';
  if ((key === 'q' || key === 'keyword') && reading.local && reading.labels?.[key]) {
    return reading.choices?.find((one) => one.options.some((option) => option.key === key))?.word ?? '';
  }
  return reading.labels?.[key] ?? String(raw);
}

/** 상품 이름의 마디 경계 — 쉼표 · 빈칸 둘레의 줄표·세로줄 */
const CLAUSE_BREAK_RE = /,\s|\s[-–—|]\s/;

/** 첫 마디로 쓸 만한 가장 짧은 길이 — 더 짧으면(「New」) 마디로 자르지 않는다 */
const CLAUSE_MIN = 8;

/** 이름 앞에 붙은 브랜드를 뗀다 — 대소문자·전각을 가리지 않는다 · 떼고 남는 것이 없으면 빈 글자 */
function stripBrandPrefix(name: string, brand: string): string {
  const want = brand.normalize('NFKC').trim().toLowerCase();
  if (!want) return name;
  const bracket = /^[[(【]([^\])】]+)[\])】]\s*/.exec(name);
  if (bracket && bracket[1].normalize('NFKC').trim().toLowerCase() === want) return name.slice(bracket[0].length).trim();
  if (!name.normalize('NFKC').toLowerCase().startsWith(want)) return name;
  const rest = name.slice(brand.trim().length);
  // 브랜드 뒤가 낱말 경계일 때만 — 「Round」 브랜드가 「Roundlab」을 자르지 않게
  if (rest && !/^[\s\-–—|:·,]/.test(rest)) return name;
  return rest.replace(/^[\s\-–—|:·,]+/, '').trim();
}

